// src/components/CustomTooltip/CustomTooltip.jsx
//
// A reusable, portal-based tooltip that replaces the browser's native
// `title` tooltip. Designed to be dropped in anywhere text might be
// truncated with ellipsis:
//
//   <CustomTooltip content={fullText}>
//     <span className="truncate">{fullText}</span>
//   </CustomTooltip>
//
// By default it only renders when the wrapped element is actually
// truncated (scrollWidth/scrollHeight > clientWidth/clientHeight), so it
// never shows up on text that already fits. Pass `onlyShowWhenTruncated={false}`
// to force it to always show (useful for icon-only buttons, avatars, etc).

import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  cloneElement,
  isValidElement,
} from "react";
import { createPortal } from "react-dom";
import "./CustomTooltip.scss";

let idCounter = 0;

export default function CustomTooltip({
  content,
  children,
  placement = "top",
  delay = 250,
  hideDelay = 80,
  disabled = false,
  onlyShowWhenTruncated = true,
  maxWidth = 320,
  offset = 10,
  className = "",
}) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, placement });

  const triggerRef = useRef(null);
  const showTimer = useRef(null);
  const hideTimer = useRef(null);
  const visibleFrame = useRef(null);
  const tooltipId = useRef(`ctt-${++idCounter}`);

  const clearTimers = () => {
    if (showTimer.current) clearTimeout(showTimer.current);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (visibleFrame.current) cancelAnimationFrame(visibleFrame.current);
  };

  const isOverflowing = useCallback((el) => {
    if (!el) return false;
    return (
      el.scrollWidth > el.clientWidth + 1 ||
      el.scrollHeight > el.clientHeight + 1
    );
  }, []);

  const computePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const half = maxWidth / 2;
    const estimatedHeight = 40;

    // Flip vertically if there isn't enough room in the preferred direction.
    let finalPlacement = placement;
    if (placement === "top" && rect.top < estimatedHeight + offset) {
      finalPlacement = "bottom";
    } else if (
      placement === "bottom" &&
      vh - rect.bottom < estimatedHeight + offset
    ) {
      finalPlacement = "top";
    }

    const top =
      finalPlacement === "top" ? rect.top - offset : rect.bottom + offset;

    // Keep horizontally within the viewport.
    let left = rect.left + rect.width / 2;
    left = Math.min(Math.max(left, half + 8), vw - half - 8);

    setCoords({ top, left, placement: finalPlacement });
  }, [placement, offset, maxWidth]);

  const show = useCallback(() => {
    if (disabled || content === null || content === undefined || content === "")
      return;
    if (onlyShowWhenTruncated && !isOverflowing(triggerRef.current)) return;

    clearTimers();
    showTimer.current = setTimeout(() => {
      computePosition();
      setMounted(true);
      // Wait a frame so the element exists in the DOM before transitioning in.
      visibleFrame.current = requestAnimationFrame(() => {
        visibleFrame.current = requestAnimationFrame(() => setVisible(true));
      });
    }, delay);
  }, [
    disabled,
    content,
    onlyShowWhenTruncated,
    isOverflowing,
    computePosition,
    delay,
  ]);

  const hide = useCallback(() => {
    clearTimers();
    hideTimer.current = setTimeout(() => {
      setVisible(false);
      hideTimer.current = setTimeout(() => setMounted(false), 150);
    }, hideDelay);
  }, [hideDelay]);

  useEffect(() => {
    if (!mounted) return undefined;
    const reposition = () => computePosition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [mounted, computePosition]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") hide();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [hide]);

  useEffect(() => clearTimers, []);

  if (!isValidElement(children)) return children ?? null;

  const child = cloneElement(children, {
    ref: (node) => {
      triggerRef.current = node;
      const { ref } = children;
      if (typeof ref === "function") ref(node);
      else if (ref && typeof ref === "object") ref.current = node;
    },
    onMouseEnter: (e) => {
      children.props.onMouseEnter?.(e);
      show();
    },
    onMouseLeave: (e) => {
      children.props.onMouseLeave?.(e);
      hide();
    },
    onFocus: (e) => {
      children.props.onFocus?.(e);
      show();
    },
    onBlur: (e) => {
      children.props.onBlur?.(e);
      hide();
    },
    "aria-describedby": mounted
      ? tooltipId.current
      : children.props["aria-describedby"],
  });

  return (
    <>
      {child}
      {mounted &&
        createPortal(
          <div
            id={tooltipId.current}
            role="tooltip"
            className={[
              "custom-tooltip",
              `custom-tooltip--${coords.placement}`,
              visible ? "is-visible" : "",
              className,
            ]
              .filter(Boolean)
              .join(" ")}
            style={{ top: coords.top, left: coords.left, maxWidth }}
          >
            {content}
            <span className="custom-tooltip__arrow" aria-hidden="true" />
          </div>,
          document.body,
        )}
    </>
  );
}
